// Pure, dependency-free helpers for the kiosk Picker's item-number formatting
// and agenda-section grouping. Split out of App.tsx (see #9416) so the
// grouping logic has a testable seam that doesn't require rendering React.
import type { AgendaItem, AgendaSection } from "./types";

// Strip trailing punctuation (period, comma, close-paren, colon) so DB-side
// itemNumber values like "1." or "12A)" don't carry stray punctuation into the
// num cell or search matching. Mirrors the online registration form's number
// cleanup (_PartialRSVPForm.cshtml itemNumberClean) so both surfaces show the
// identical label for the same item. See design-review H2.
export const cleanItemNumber = (s?: string | null): string => {
  if (!s) return "";
  return s.replace(/[.,):]+\s*$/, "");
};

/** Render-safe human label for an agenda item's number cell.
 *
 *  Some items (e.g. "No-Agenda Public Communication") have NO itemNumber in
 *  the DB and used to fall back to the bare DB primary key — a 7–8 digit
 *  number that blows out the 80px num cell and confuses participants
 *  ("Item 10602391" means nothing to them).
 *
 *  Rules:
 *   - Empty/null/whitespace → "" (no placeholder — an item with no number
 *     just shows no number; AB#9416 follow-up dropped the em-dash, which
 *     read as an odd/broken value rather than "nothing here").
 *   - itemNumber exactly equals this item's own DB id → "" (that's the
 *     leaked-primary-key case, not a real label). AB#9416: this used to be a
 *     ">5 all-numeric chars" length heuristic, which also hid legitimate
 *     long numeric item/ordinance numbers that simply don't happen to match
 *     the row's id. Comparing against the actual id targets only the real
 *     failure mode.
 *   - Otherwise → the cleaned itemNumber, no prefix. The earlier "#"-prefix
 *     variant was removed so what users see matches what they can search
 *     for; the underlying DB value never contains a "#". */
export const displayItemNumber = (raw?: string | null, id?: number): string => {
  const cleaned = cleanItemNumber(raw);
  if (!cleaned) return "";
  if (id !== undefined && cleaned === String(id)) return "";
  return cleaned;
};

export interface ItemGroup {
  key: string;
  label: string;
  items: AgendaItem[];
}

/** Group agenda items by their real agenda section, in section display order,
 *  matching the grouping the online registration form uses
 *  (_PartialRSVPForm.cshtml:630-887). Items keep the order they arrive in
 *  (the server already sorts agendaItems by DisplayOrder, Id), so no
 *  re-sorting happens here — only grouping.
 *
 *  - Sections with zero matching items are omitted (no empty headers).
 *  - Items whose sectionId doesn't match any known section (0, or a section
 *    the /data payload didn't include) land in one trailing unsectioned
 *    group with an empty label — rendered with no header, same as the
 *    online form's unsectioned block. */
export const groupItemsBySection = (
  items: AgendaItem[],
  sections: AgendaSection[]
): ItemGroup[] => {
  const orderedSections = [...sections].sort(
    (a, b) => a.displayOrder - b.displayOrder || a.id - b.id
  );
  const knownSectionIds = new Set(orderedSections.map((s) => s.id));

  const out: ItemGroup[] = [];
  for (const section of orderedSections) {
    const sectionItems = items.filter((it) => it.sectionId === section.id);
    if (!sectionItems.length) continue;
    out.push({
      key: `section-${section.id}`,
      label: section.title ?? "",
      items: sectionItems
    });
  }

  const unsectioned = items.filter((it) => !knownSectionIds.has(it.sectionId));
  if (unsectioned.length) {
    out.push({ key: "unsectioned", label: "", items: unsectioned });
  }
  return out;
};

/** Ids of the user's selected agenda items, in agenda order (section, then
 *  item order within the section) rather than raw DB id order.
 *
 *  AB#9416: the "My picks" sheet, Review screen, and Done screen all used to
 *  do `Object.keys(selections).map(Number).sort((a, b) => a - b)` -- sorting
 *  by the item's own database id, which has no guaranteed relationship to
 *  agenda position (admin-reordered items, or items synced from Legistar/
 *  PrimeGov whose row id reflects sync/creation order, not the published
 *  agenda). Reuses groupItemsBySection so all four surfaces (Picker list,
 *  My picks, Review, Done) agree on one ordering. */
export const orderSelectedIds = (
  selections: Record<number, unknown>,
  agendaItems: AgendaItem[],
  sections: AgendaSection[]
): number[] => {
  const selectedIds = new Set(
    Object.keys(selections).map((id) => parseInt(id, 10))
  );
  const selectedItems = agendaItems.filter((it) => selectedIds.has(it.id));
  return groupItemsBySection(selectedItems, sections).flatMap((g) =>
    g.items.map((it) => it.id)
  );
};
